import { logger } from "@/lib/logger";
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface AnalyticsEvent {
  event_type: string;
  event_category?: string;
  event_action?: string;
  event_label?: string;
  event_value?: number;
  page_url: string;
  user_id?: string;
  session_id: string;
  metadata?: Record<string, any>;
}

export function useAnalyticsTracking() {
  const location = useLocation();

  // Generate or retrieve session ID
  const getSessionId = useCallback(() => {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }, []);

  // Track page view
  const trackPageView = useCallback(async (customPath?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const event: AnalyticsEvent = {
        event_type: 'page_view',
        event_category: 'navigation',
        page_url: customPath || location.pathname,
        user_id: user?.id,
        session_id: getSessionId(),
        metadata: {
          referrer: document.referrer,
          user_agent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          timestamp: Date.now()
        }
      };

      const { error } = await supabase
        .from('conversion_events')
        .insert(event);

      if (error) throw error;
    } catch (error) {
      logger.error('Failed to track page view:', error);
    }
  }, [location.pathname, getSessionId]);

  // Track custom event
  const trackEvent = useCallback(async (
    eventType: string,
    category?: string,
    action?: string,
    label?: string,
    value?: number,
    metadata?: Record<string, any>
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const event: AnalyticsEvent = {
        event_type: eventType,
        event_category: category,
        event_action: action,
        event_label: label,
        event_value: value,
        page_url: location.pathname,
        user_id: user?.id,
        session_id: getSessionId(),
        metadata: {
          ...metadata,
          timestamp: Date.now()
        }
      };

      const { error } = await supabase
        .from('conversion_events')
        .insert(event);

      if (error) throw error;
    } catch (error) {
      logger.error('Failed to track event:', error);
    }
  }, [location.pathname, getSessionId]);

  // Track user interaction
  const trackInteraction = useCallback((
    element: string,
    action: string,
    metadata?: Record<string, any>
  ) => {
    trackEvent('user_interaction', 'engagement', action, element, undefined, metadata);
  }, [trackEvent]);

  // Track conversion
  const trackConversion = useCallback((
    conversionType: string,
    value?: number,
    metadata?: Record<string, any>
  ) => {
    trackEvent('conversion', 'business', conversionType, undefined, value, {
      ...metadata,
      is_conversion: true
    });
  }, [trackEvent]);

  // Track error with smart sampling to prevent feedback loops
  const trackError = useCallback((
    error: Error,
    errorBoundary?: string,
    metadata?: Record<string, any>
  ) => {
    // Smart error filtering to prevent feedback loops
    const errorKey = error.message.substring(0, 50);
    
    // Don't log conversion_events query errors (breaks feedback loop)
    if (errorKey.includes('conversion_events') || 
        errorKey.includes('analytics') ||
        errorKey.includes('system_metrics')) {
      return;
    }
    
    // Sample performance warnings at 5% to reduce noise
    if (errorKey.includes('Long Task') || 
        errorKey.includes('slow') ||
        errorKey.includes('performance')) {
      if (Math.random() > 0.05) {
        return; // Skip 95% of performance warnings
      }
    }
    
    trackEvent('error', 'system', 'error_occurred', error.name, undefined, {
      error_message: error.message,
      error_stack: error.stack,
      error_boundary: errorBoundary,
      ...metadata
    });
  }, [trackEvent]);

  // Auto-track page views on route changes
  useEffect(() => {
    trackPageView();
  }, [trackPageView]);

  return {
    trackPageView,
    trackEvent,
    trackInteraction,
    trackConversion,
    trackError,
    sessionId: getSessionId()
  };
}